// Helper per il file manager dei documenti di flotta (autisti / mezzi / hub).
//
// Firebase Storage non ha cartelle reali: le "cartelle" sono prefissi di path.
// Una cartella vuota viene materializzata con un file placeholder `.keep`, che
// resta nascosto nel listing.
//
// Elenco, upload ed eliminazione passano per `window.firebase` (SDK compat),
// coerentemente col resto del progetto. Spostamento e rinomina no: richiedono
// di copiare i byte, e il download `?alt=media` è soggetto alla CORS del
// bucket. Vanno quindi a un endpoint dedicato che usa il `move` nativo di GCS
// (vedi `/v2/fleet/storage/*`).

import { doPost } from '../DataManager';

const PLACEHOLDER = '.keep';

const storage = () => window.firebase.storage();

/** Errore con codice applicativo, così la UI può distinguere i casi. */
export class StorageOpError extends Error {
  constructor(code, message, details) {
    super(message);
    this.name = 'StorageOpError';
    this.code = code; // 'name_taken' | 'move_failed'
    this.details = details;
  }
}

/**
 * Elenca il contenuto di un prefisso.
 * @param {string} prefix path completo della cartella corrente
 * @returns {Promise<{folders: {name,fullPath}[], files: {name,url,fullPath,size,updated,contentType}[]}>}
 */
export const listFolder = async (prefix) => {
  const res = await storage().ref(prefix).listAll();

  const folders = res.prefixes.map((p) => ({ name: p.name, fullPath: p.fullPath }));

  const files = await Promise.all(
    res.items
      .filter((item) => item.name !== PLACEHOLDER)
      .map(async (item) => {
        const [url, meta] = await Promise.all([
          item.getDownloadURL(),
          item.getMetadata().catch(() => ({})),
        ]);
        return {
          name: item.name,
          fullPath: item.fullPath,
          url,
          size: meta.size ?? null,
          updated: meta.updated ?? null,
          contentType: meta.contentType ?? null,
        };
      })
  );

  return { folders, files };
};

/**
 * Carica uno o più file nel prefisso corrente.
 * @returns {Promise<{name,url,fullPath}[]>}
 */
export const uploadFiles = async (prefix, files) => {
  const uploaded = await Promise.all(
    Array.from(files).map(async (file) => {
      const fullPath = `${prefix}/${file.name}`;
      const ref = storage().ref(fullPath);
      await ref.put(file);
      const url = await ref.getDownloadURL();
      return { name: file.name, url, fullPath };
    })
  );
  return uploaded;
};

/** Elimina un singolo file dal suo path completo. */
export const deleteFile = (fullPath) => storage().ref(fullPath).delete();

/** Download URL di un singolo file dal suo path completo. */
export const getDownloadUrl = (fullPath) => storage().ref(fullPath).getDownloadURL();

/**
 * Risolve in blocco i download URL di più path.
 *
 * Un file mancante (cancellato a mano dal bucket, path storto scritto da un
 * client vecchio) non deve far sparire l'intera galleria: l'errore si isola sul
 * singolo path, che torna `url: null` e viene semplicemente saltato dalla UI.
 *
 * @param {string[]} fullPaths
 * @returns {Promise<{fullPath: string, url: string|null}[]>}
 */
export const resolveDownloadUrls = (fullPaths) =>
  Promise.all(
    (fullPaths ?? []).map(async (fullPath) => {
      try {
        return { fullPath, url: await getDownloadUrl(fullPath) };
      } catch (error) {
        console.error('Foto non risolvibile su Storage:', fullPath, error);
        return { fullPath, url: null };
      }
    })
  );

/**
 * Crea una cartella (vuota) sotto il prefisso corrente, scrivendo il placeholder.
 * @returns {Promise<{name, fullPath}>}
 */
export const createFolder = async (prefix, name) => {
  await assertNameAvailable(prefix, name);
  const folderPath = `${prefix}/${name}`;
  const empty = new Blob([''], { type: 'text/plain' });
  await storage().ref(`${folderPath}/${PLACEHOLDER}`).put(empty);
  return { name, fullPath: folderPath };
};

/**
 * Elimina ricorsivamente una cartella e tutto il suo contenuto.
 * Storage non ha delete-cartella nativo: si scende ricorsivamente e si
 * cancellano tutti gli item incontrati.
 */
export const deleteFolder = async (prefix) => {
  const res = await storage().ref(prefix).listAll();
  await Promise.all(res.items.map((item) => item.delete()));
  await Promise.all(res.prefixes.map((sub) => deleteFolder(sub.fullPath)));
};

/**
 * Verifica che dentro `parentPrefix` non esista già una voce con quel nome.
 * Storage sovrascrive in silenzio (`put`) e non ha un concetto di "cartella
 * esistente": senza questo controllo una rinomina su un nome già usato fonde le
 * due cartelle o sovrascrive il file, senza alcun errore.
 * @throws {StorageOpError} code `name_taken`
 */
export const assertNameAvailable = async (parentPrefix, name, { ignoreFullPath } = {}) => {
  const res = await storage().ref(parentPrefix).listAll();
  const taken = [...res.prefixes, ...res.items].some(
    (entry) => entry.name === name && entry.fullPath !== ignoreFullPath
  );
  if (taken) {
    throw new StorageOpError('name_taken', `Esiste già una voce chiamata "${name}"`, { name });
  }
};

/**
 * Sposta/rinomina lato server.
 *
 * Il browser non può copiare i byte da solo: il download `?alt=media` è
 * soggetto alla CORS del bucket (`getBlob`/`getBytes` dell'SDK passano dallo
 * stesso endpoint), e comunque farebbe transitare ogni file dalla connessione
 * dell'utente. L'Admin SDK ha `move` nativo su GCS: la rinomina non scarica
 * nulla ed è immediata anche su cartelle da decine di file.
 */
const requestMove = async (endpoint, payload, { name }) => {
  try {
    // In caso di successo `doPost` risolve già il `data` dell'envelope `Out`.
    return await doPost(`/v2/fleet/storage/${endpoint}`, payload);
  } catch (error) {
    // Su `success: false` DataManager rigetta con il messaggio applicativo:
    // `name_taken` è un esito previsto (nome già usato), non un errore di rete.
    const message = typeof error === 'string' ? error : error?.message;
    if (message === 'name_taken') {
      throw new StorageOpError('name_taken', `Esiste già una voce chiamata "${name}"`, { name });
    }
    throw new StorageOpError('move_failed', message || 'Spostamento non riuscito', error);
  }
};

/**
 * Sposta/rinomina un singolo file.
 * @param {string} fromFullPath path sorgente
 * @param {string} toPrefix     cartella destinazione (path completo)
 * @param {string} [newName]    nuovo nome file (default: nome originale)
 * @returns {Promise<{name: string, fullPath: string}>}
 */
export const moveFile = async (fromFullPath, toPrefix, newName) => {
  const name = newName || fromFullPath.split('/').pop();
  const toPath = `${toPrefix}/${name}`;
  if (toPath === fromFullPath) return { name, fullPath: toPath };

  await requestMove('move-file', { from: fromFullPath, toPrefix, newName: name }, { name });
  return { name, fullPath: toPath };
};

/**
 * Sposta/rinomina una cartella e tutto il suo contenuto.
 * @param {string} prefix          path completo cartella sorgente
 * @param {string} toParentPrefix  path completo cartella genitore destinazione
 * @param {string} [newName]       nuovo nome cartella (default: nome originale)
 * @returns {Promise<{name: string, fullPath: string, moved: number}>}
 */
export const moveFolder = async (prefix, toParentPrefix, newName) => {
  const name = newName || prefix.split('/').pop();
  const destPrefix = `${toParentPrefix}/${name}`;
  if (destPrefix === prefix) return { name, fullPath: destPrefix, moved: 0 };

  const res = await requestMove(
    'move-folder',
    { from: prefix, toParentPrefix, newName: name },
    { name }
  );
  return { name, fullPath: destPrefix, moved: res?.moved ?? 0 };
};
